// @flow

import React, { useCallback, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { Location } from '@reach/router';

import { Logo as LogoData, Nav as NavData } from 'data/global/nav';
import { Logo, LogoMarkets } from 'components/Svg';
import { absolute, buttonDefault, Color, flex, hover, hoverChild, Layout, maxWidthContainer, Transition, Width } from 'styles';
import { Link } from 'components/Gui';
import Background from './Background';
import { useNavState, usePageState } from 'contexts';
import { BODY_ID, DESKTOP_NAV_ID, LIGHT_DARK, TAB } from 'Constants';

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: ${Layout.NAV_HEIGHT_DESKTOP}px;
  z-index: ${Layout.Z_INDEX_DESKTOP_NAV};
  display: none;

  @media print, screen and (min-width: ${Width.BREAK_MOBILE_NAV}px) {
    ${flex()};
  }

  > div {
    &:nth-of-type(2) {
      height: 100%;
      position: relative;
      z-index: 1;
      ${maxWidthContainer(Width.XL + 100)};
      ${flex('space-between', 'center')};

      > div {
        &:nth-of-type(1) {
          height: 100%;
          ${flex('flex-start', 'center')};
          width: 100%;

          > a.logo {
            margin-right: 48px;
            svg {
              width: 85px;
              ${({ theme, atTop }) =>
                theme === LIGHT_DARK.DARK && atTop
                  ? css`
                      ${hoverChild('path, rect, polygon', 'fill', Color.WHITE, Color.BLURPLE_LIGHT)};
                    `
                  : css`
                      ${hoverChild('path, rect, polygon', 'fill', Color.WHITE, Color.MARKETS_GREEN)};
                    `}
            }
          }

          > div {
            flex: 1 1 auto;
            margin-right: 133px;
            ${flex()};
            > .link {
              ${buttonDefault};
              position: relative;
              margin-right: 54px;
              ${({ theme, atTop }) =>
                theme === LIGHT_DARK.DARK && !atTop
                  ? css`
                      ${hover('color', Color.WHITE, Color.YELLOW_MELLOW)};
                    `
                  : css`
                      ${hover('color', Color.WHITE, Color.MARKETS_RED)};
                    `}
            }

            &:last-child {
              margin-right: 0;
            }

            > .link {
              position: relative;

              > div {
                position: absolute;
                top: 16px;
                left: 0;
                width: 240px;
                opacity: 0;
                pointer-events: none;
                transition: opacity ${Transition.DEFAULT};
                padding-top: 30px;

                > div {
                  padding: 24px;
                  background: rgba(255, 255, 255, 0.9);

                  a {
                    text-align: left;
                    ${buttonDefault};
                    font-weight: 400;
                    font-size: 1.4rem;
                    display: block;
                    ${hover('color', Color.BLACK_SOUL, Color.MARKETS_RED)};
                    margin-bottom: 10px;

                    &:last-of-type {
                      margin-bottom: 0;
                    }
                  }

                  a.link.active {
                    color: ${Color.MARKETS_RED} !important;
                  }
                }

                p {
                  ${buttonDefault};
                  color: ${Color.MARKETS_RED};
                  text-transform: none;
                  text-align: left;
                }
              }

              &:hover {
                > div {
                  opacity: 1;
                  pointer-events: auto;
                }
              }
            }

            > .link.active {
              color: ${Color.MARKETS_RED} !important;
            }

            > .link.external {
              ${({ theme, atTop }) =>
                theme === LIGHT_DARK.DARK && !atTop
                  ? css`
                      ${hover('color', Color.MARKETS_GREEN, Color.WHITE)};
                    `
                  : css`
                      ${hover('color', Color.MARKETS_GREEN, Color.WHITE)};
                    `}
            }
          }
        }
      }
    }
  }
`;

const StaticWrapper = styled.div`
  ${absolute(0, 0, 'unset', 0)};
  height: ${Layout.NAV_HEIGHT_DESKTOP}px;

  > div {
    height: 100%;
    position: relative;
    z-index: 1;
    ${maxWidthContainer(Width.XL + 100)};
    ${flex('flex-start', 'center')};

    a {
      margin-right: 48px;
      svg {
        width: 85px;
        ${hoverChild('path', 'fill', Color.BLURPLE_MOODY, Color.PURPLE_FIGURE)};
      }
    }
  }
`;

const DesktopNav = (props: any) => {
  const [skipAheadShowing, setSkipAheadShowing] = useState(false);
  const { renderDesktop, atTop } = useNavState();
  const {
    headerConfig: { toggleHidden, theme },
  } = usePageState();
  const showSkipAhead = useCallback(
    ({ key, keyCode }) => {
      if (TAB.includes(key || keyCode)) {
        setSkipAheadShowing(true);
      }
    },
    [setSkipAheadShowing]
  );
  const focusFirstElement = useCallback(() => {
    const els = document.getElementById(BODY_ID).querySelectorAll('button, [href]');
    if (els[0]) els[0].focus();
  }, []);
  return useMemo(
    () =>
      renderDesktop ? (
        <>
          {toggleHidden.staticLogo && (
            <Wrapper theme={theme} atTop={atTop} {...props} id={DESKTOP_NAV_ID} onKeyUp={showSkipAhead}>
              {!toggleHidden.background && <Background />}
              <div>
                <div>
                  {!toggleHidden.logo && (
                    <Link className="logo" {...LogoData}>
                      {LogoMarkets}
                    </Link>
                  )}
                  <div>
                    {skipAheadShowing && (
                      <Link
                        className="link"
                        url={`#${BODY_ID}`}
                        onClick={focusFirstElement}
                        label="Navigate to the main content area.">
                        Main Content
                      </Link>
                    )}
                    {!toggleHidden.nav && (
                      <>
                        {NavData.map(({ title, url, children }, index) => (
                          <Location key={`MenuNav-${index}`}>
                            {({ location: { pathname } }) => (
                              <Link
                                className={url.includes('http') ? 'link external' : `link${pathname === url ? ' active' : ''}`}
                                url={children ? null : url}>
                                {children && (
                                  <div>
                                    <div>
                                      <p>{children.title}</p>
                                      {children.links.map(({ title, url }, index) => (
                                        <Link
                                          className={
                                            url.includes('http') ? 'link external' : `link${pathname === url ? ' active' : ''}`
                                          }
                                          key={`InnerNav-${index}`}
                                          url={url}>
                                          {title}
                                        </Link>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {title}
                              </Link>
                            )}
                          </Location>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Wrapper>
          )}
          {!toggleHidden.staticLogo && (
            <StaticWrapper {...props} id={DESKTOP_NAV_ID}>
              <div>
                <Link {...LogoData}>{Logo}</Link>
              </div>
            </StaticWrapper>
          )}
        </>
      ) : null,
    [focusFirstElement, props, renderDesktop, showSkipAhead, skipAheadShowing, toggleHidden, theme, atTop]
  );
};

export default DesktopNav;
